"use client";

import { ProgressProvider } from "@bprogress/next/app";
import { HeroUIProvider } from "@heroui/react";
import { ToastProvider } from "@heroui/toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

function ThemeWrapper({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isAdmin = pathname?.includes("/admin");
  const locale = useLocale();

  // Convert locale to BCP 47 format (fi -> fi-FI, en -> en-US)
  const bcp47Locale = locale === "fi" ? "fi-FI" : "en-US";

  // Directly set theme class on document - no state, no storage, no sync issues
  useEffect(() => {
    const theme = isAdmin ? "dark" : "medieval";
    document.documentElement.classList.remove("dark", "medieval");
    document.documentElement.classList.add(theme);
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.style.colorScheme = isAdmin ? "dark" : "light";
  }, [isAdmin]);

  return <HeroUIProvider locale={bcp47Locale}>{children}</HeroUIProvider>;
}

export function Providers({ children }: { children: React.ReactNode }) {
  // Create a client instance per render to avoid sharing state between requests
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000, // 1 minute
            gcTime: 5 * 60 * 1000, // 5 minutes (previously cacheTime)
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeWrapper>
        <ProgressProvider
          height="4px"
          color="hsl(var(--heroui-primary))"
          delay={200}
          spinnerPosition="bottom-left"
        >
          <ToastProvider
            placement="top-right"
            toastOffset={100}
            toastProps={{
              radius: "sm",
              classNames: {
                base: "bg-[var(--bg-card)] border border-[var(--border-gold)] shadow-2xl backdrop-blur-sm font-spectral",
                title: "text-primary font-cinzel font-semibold",
                description: "text-secondary",
                closeButton:
                  "text-secondary hover:text-[var(--gold)] transition-colors",
              },
            }}
          />
          {children}
        </ProgressProvider>
      </ThemeWrapper>
    </QueryClientProvider>
  );
}
